import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageCircle, Send, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import LightBackground from '@/components/LightBackground';
import { motion } from 'framer-motion';

interface Ticket {
  id: string;
  client_id: string;
  subject: string;
  status: string;
  created_at: string;
  client_name?: string;
  client_email?: string;
}

interface Message {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: string;
  message: string;
  created_at: string;
}

const AdminSupport = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('open');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, isAdmin, authLoading, navigate]);

  // Fetch tickets with client info
  useEffect(() => {
    const fetchTickets = async () => {
      let query = supabase
        .from('support_tickets')
        .select('*, clients(name, email)')
        .order('updated_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data } = await query;
      if (data) {
        setTickets(
          data.map((t: any) => ({
            ...t,
            client_name: t.clients?.name,
            client_email: t.clients?.email,
          }))
        );
      }
    };
    fetchTickets();
  }, [filter]);

  // Fetch messages for selected ticket
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedTicket) return;
      const { data } = await supabase
        .from('support_messages')
        .select('*')
        .eq('ticket_id', selectedTicket.id)
        .order('created_at', { ascending: true });
      if (data) setMessages(data);
    };
    fetchMessages();
  }, [selectedTicket]);

  // Realtime subscription
  useEffect(() => {
    if (!selectedTicket) return;

    const channel = supabase
      .channel(`admin-messages-${selectedTicket.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_messages',
          filter: `ticket_id=eq.${selectedTicket.id}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedTicket]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!selectedTicket || !newMessage.trim() || !user) return;
    setLoading(true);

    const { error } = await supabase.from('support_messages').insert({
      ticket_id: selectedTicket.id,
      sender_id: user.id,
      sender_type: 'admin',
      message: newMessage.trim(),
    });

    if (error) {
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } else {
      setNewMessage('');
    }
    setLoading(false);
  };

  const closeTicket = async () => {
    if (!selectedTicket) return;
    setLoading(true);

    const { error } = await supabase
      .from('support_tickets')
      .update({ status: 'closed' })
      .eq('id', selectedTicket.id);

    if (error) {
      toast({ title: 'Error', description: 'Failed to close ticket', variant: 'destructive' });
    } else {
      setSelectedTicket({ ...selectedTicket, status: 'closed' });
      setTickets((prev) =>
        prev.map((t) => (t.id === selectedTicket.id ? { ...t, status: 'closed' } : t))
      );
      toast({ title: 'Ticket closed' });
    }
    setLoading(false);
  };

  const reopenTicket = async () => {
    if (!selectedTicket) return;
    setLoading(true);

    const { error } = await supabase
      .from('support_tickets')
      .update({ status: 'open' })
      .eq('id', selectedTicket.id);

    if (error) {
      toast({ title: 'Error', description: 'Failed to reopen ticket', variant: 'destructive' });
    } else {
      setSelectedTicket({ ...selectedTicket, status: 'open' });
      setTickets((prev) =>
        prev.map((t) => (t.id === selectedTicket.id ? { ...t, status: 'open' } : t))
      );
      toast({ title: 'Ticket reopened' });
    }
    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden text-foreground">
      <LightBackground variant="subtle" />
      
      {/* Header */}
      <motion.header 
        className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold tracking-tight">
              Support <span className="text-primary">Tickets</span>
            </h1>
          </div>
          <div className="flex gap-2">
            {(['open', 'closed', 'all'] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className="capitalize"
              >
                {f}
              </Button>
            ))}
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-4 py-6 relative z-10">
        <div className="grid md:grid-cols-3 gap-6 h-[calc(100vh-140px)]">
          {/* Tickets List */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Card className="md:col-span-1 bg-white border-border shadow-sm overflow-hidden rounded-2xl h-full">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center">
                    <MessageCircle className="h-3 w-3 text-primary" />
                  </div>
                  Tickets ({tickets.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-280px)]">
                  <div className="p-2 space-y-2">
                    {tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={cn(
                          'w-full text-left p-3 rounded-xl border transition-all',
                          selectedTicket?.id === ticket.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30 bg-white'
                        )}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-sm font-medium truncate">{ticket.subject}</span>
                          <Badge
                            className={
                              ticket.status === 'open'
                                ? 'bg-green-100 text-green-700 border-green-200'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }
                          >
                            {ticket.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {ticket.client_name || 'Unknown'} • {ticket.client_email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </p>
                      </button>
                    ))}
                    {tickets.length === 0 && (
                      <p className="text-center text-muted-foreground text-sm py-8">
                        No {filter !== 'all' ? filter : ''} tickets
                      </p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Messages Panel */}
          <motion.div
            className="md:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="bg-white border-border shadow-sm flex flex-col overflow-hidden rounded-2xl h-full">
              {selectedTicket ? (
                <>
                  <CardHeader className="border-b border-border flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-medium">{selectedTicket.subject}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedTicket.client_name} • {selectedTicket.client_email}
                      </p>
                    </div>
                    {selectedTicket.status === 'open' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={closeTicket}
                        disabled={loading}
                        className="gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Close Ticket
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={reopenTicket} disabled={loading}>
                        Reopen
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col p-0 min-h-0">
                    <ScrollArea className="flex-1 p-4">
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={cn(
                              'max-w-[80%] p-3 rounded-xl',
                              msg.sender_type === 'admin'
                                ? 'ml-auto bg-primary/10 text-foreground'
                                : 'bg-slate-100 text-foreground'
                            )}
                          >
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {msg.sender_type === 'admin' ? 'You' : selectedTicket.client_name}
                            </p>
                            <p className="text-sm">{msg.message}</p>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        ))}
                        <div ref={messagesEndRef} />
                      </div>
                    </ScrollArea>

                    {selectedTicket.status === 'open' && (
                      <div className="flex gap-2 p-4 border-t border-border">
                        <Textarea
                          placeholder="Type your reply..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          className="flex-1 min-h-[60px] max-h-[100px] bg-slate-50 border-border"
                        />
                        <Button onClick={sendMessage} disabled={loading} size="icon">
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-muted-foreground">Select a ticket to view messages</p>
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupport;
